import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Select,
  MenuItem,
  FormControl,
} from "@mui/material";
import UploadOutlinedIcon from "@mui/icons-material/UploadOutlined";
import DeleteOutlineOutlinedIcon from "@mui/icons-material/DeleteOutlineOutlined";
import DescriptionIcon from "@mui/icons-material/Description";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useSelector } from "react-redux";
import { darkTheme, lightTheme } from "../../theme/Theme";
import { showErrorToast, showSuccessToast } from "../../utils/Toast/toast";
import {
  getLanguageData,
  validateVttFileBeforeUpload,
  uploadAndValidateVttFile,
  handleVttFileDelete,
  prepareLanguageOptionsForDropdown,
} from "../../utils/function.js";
import { useVttFileHandlers } from "../../hooks/useVttFileHandlers";

const SubtitleManagement = ({
  subtitleData = [],
  setSubtitleData = () => {},
  videoSubtitlesFileDetails = [],
  setVideoSubtitlesFileDetails = () => {},
}) => {
  const [subtitleFiles, setSubtitleFiles] = useState(videoSubtitlesFileDetails);
  const [languageOptions, setLanguageOptions] = useState([]);
  const [isUploading, setIsUploading] = useState(false);
  const [viewingFileId, setViewingFileId] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState(null);
  const [deletingFileId, setDeletingFileId] = useState(null);
  const isDarkTheme = useSelector((state) => state.theme);

  // Load language options on component mount
  useEffect(() => {
    const fetchLanguageOptions = async () => {
      try {
        const activeItems = await getLanguageData();
        setLanguageOptions(activeItems);
      } catch (error) {
        console.error("Error fetching language options:", error);
        showErrorToast("Failed to load language options");
      }
    };

    fetchLanguageOptions();
  }, []);

  // Update subtitleFiles when videoSubtitlesFileDetails changes
  useEffect(() => {
    if (videoSubtitlesFileDetails && videoSubtitlesFileDetails.length > 0) {
      setSubtitleFiles(videoSubtitlesFileDetails);
    }
  }, [videoSubtitlesFileDetails]);

  const getThemeColor = (lightColor, darkColor) =>
    isDarkTheme ? darkColor : lightColor;

  const handleFileUpload = async (acceptedFiles, languageId) => {
    const file = acceptedFiles[0];

    if (!file) return;

    // Validate file before upload
    const validation = validateVttFileBeforeUpload(file, 10);
    if (!validation.isValid) {
      showErrorToast(validation.error);
      return;
    }

    // Check if a file with the same language already exists
    const existingFile = subtitleFiles.find(
      (file) => file.languageId === languageId
    );
    if (existingFile) {
      const languageName =
        languageOptions.find((lang) => lang.id === languageId)?.name ||
        "Unknown";
      showErrorToast(
        `A subtitle file for ${languageName} already exists. Please delete the existing file first or choose a different language.`
      );
      return;
    }

    setIsUploading(true);

    try {
      const result = await uploadAndValidateVttFile(
        file,
        languageId,
        languageOptions,
        "subtitle"
      );

      if (!result.success) {
        showErrorToast(result.error);
        setIsUploading(false);
        return;
      }

      const newSubtitleFile = result.fileData;

      setSubtitleFiles((prev) => [...prev, newSubtitleFile]);
      setSelectedLanguage(null);

      // Update parent component's videoSubtitlesFileDetails
      if (typeof setVideoSubtitlesFileDetails === "function") {
        setVideoSubtitlesFileDetails((prev) => [...prev, newSubtitleFile]);
      }

      if (typeof setSubtitleData === "function") {
        // Create the simplified array structure for API
        const subtitleDataItem = {
          fileId: result.fileId,
          languageId: languageId,
        };
        setSubtitleData((prev) => [...prev, subtitleDataItem]);
      }
    } catch (error) {
      console.error("Error uploading subtitle file:", error);
      showErrorToast("Failed to upload subtitle file");
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileDelete = async (fileId) => {
    setDeletingFileId(fileId);

    try {
      const result = await handleVttFileDelete(
        fileId,
        subtitleFiles,
        setSubtitleFiles,
        setVideoSubtitlesFileDetails,
        setSubtitleData,
        "subtitle-file-input"
      );

      if (result.success) {
        showSuccessToast("File deleted successfully");
      } else {
        showErrorToast(result.error);
      }
    } catch (error) {
      console.error("Error deleting file:", error);
      showErrorToast("Failed to delete file. Please try again.");
    } finally {
      setDeletingFileId(null);
    }
  };

  // Use shared handlers from custom hook
  const { handleLanguageSelect, handleFileInputChange, handleViewDocument } =
    useVttFileHandlers({
      handleFileUpload,
      selectedLanguage,
      setSelectedLanguage,
      setViewingFileId,
    });

  const handleUploadClick = () => {
    if (!selectedLanguage) {
      showErrorToast("Please select a language first");
      return;
    }

    // Trigger file input
    document.getElementById("subtitle-file-input").click();
  };

  const languageOptionsForDropdown = prepareLanguageOptionsForDropdown(
    languageOptions,
    subtitleFiles
  );

  // Check if all languages have been uploaded
  const allLanguagesUploaded =
    languageOptions.length > 0 &&
    languageOptions.every((lang) =>
      subtitleFiles.some((file) => file.languageId === lang.id)
    );

  return (
    <Box
      data-testid="subtitle-management"
      sx={{ width: "100%", mb: 3, ml: 2, pr: 2 }}
    >
      {/* Subtitles document section */}
      <Box sx={{ mb: 3 }}>
        <Typography
          variant="h6"
          sx={{
            fontSize: "18px",
            fontWeight: 600,
            color: getThemeColor(
              lightTheme.textColorLayer1,
              darkTheme.textColorLayer1
            ),
            mb: 2,
            fontFamily: "inherit",
          }}
        >
          Subtitles document
        </Typography>

        {/* Language selection and upload controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
            mb: 2,
          }}
        >
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              color: getThemeColor(
                lightTheme.textColorLayer1,
                darkTheme.textColorLayer1
              ),
              fontFamily: "inherit",
              minWidth: "80px",
            }}
          >
            Language
          </Typography>

          <FormControl
            sx={{
              minWidth: { xs: "100%", sm: 200 },
              width: { xs: "100%", sm: "auto" },
              "& .MuiOutlinedInput-root": {
                borderRadius: "8px",
                "& fieldset": {
                  borderColor: "#D7E5F8",
                },
                "&:hover fieldset": {
                  borderColor: "#1976D2",
                },
                "&.Mui-focused fieldset": {
                  borderColor: "#1976D2",
                },
              },
            }}
          >
            <Select
              value={selectedLanguage?.id || ""}
              onChange={(e) => {
                const selectedLang = languageOptions.find(
                  (lang) => lang.id === e.target.value
                );
                setSelectedLanguage(selectedLang || null);
              }}
              displayEmpty
              IconComponent={KeyboardArrowDownIcon}
              sx={{
                fontSize: "14px",
                fontFamily: "inherit",
                "& .MuiSelect-select": {
                  padding: "8px 12px",
                },
              }}
            >
              <MenuItem value="" disabled>
                <em>Select</em>
              </MenuItem>
              {languageOptionsForDropdown.map((option) => (
                <MenuItem
                  key={option.id}
                  value={option.id}
                  disabled={option.disabled}
                  sx={{
                    fontSize: "14px",
                    fontFamily: "inherit",
                  }}
                >
                  {option.displayText}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Button
            variant="contained"
            onClick={handleUploadClick}
            disabled={!selectedLanguage || allLanguagesUploaded}
            sx={{
              borderRadius: "20px",
              fontWeight: 600,
              fontSize: "12px",
              py: 1,
              px: 4,
              textTransform: "none",
              backgroundColor: "#1976D2",
              width: { xs: "100%", sm: "auto" },
              "&:hover": {
                backgroundColor: "#1565C0",
              },
              "&:disabled": {
                backgroundColor: "#E0E0E0",
                color: "#9E9E9E",
              },
            }}
            startIcon={<UploadOutlinedIcon />}
          >
            Upload subtitle file
          </Button>
        </Box>

        <input
          id="subtitle-file-input"
          type="file"
          accept=".vtt"
          style={{ display: "none" }}
          onChange={handleFileInputChange}
        />

        {/* File list */}
        {subtitleFiles.length > 0 && (
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 1,
              width: { xs: "100%", md: "40%", lg: "30%" },
            }}
          >
            {subtitleFiles.map((subtitleFile) => (
              <Box
                key={subtitleFile.id}
                sx={{
                  display: "flex",
                  alignItems: { xs: "flex-start", sm: "center" },
                  flexDirection: { xs: "column", sm: "row" },
                  gap: { xs: 1.5, sm: 2 },
                  py: 1,
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: { xs: 1, sm: 1.5, md: 2 },
                    minWidth: 0,
                    flex: { xs: "1 1 100%", sm: "1 1 auto" },
                    flexWrap: "nowrap",
                    overflow: "hidden",
                  }}
                >
                  <DescriptionIcon
                    sx={{ color: "#666", fontSize: "20px", flexShrink: 0 }}
                  />
                  <Typography
                    title={subtitleFile.displayName || subtitleFile.fileName}
                    sx={{
                      fontSize: "14px",
                      fontFamily: "inherit",
                      color: getThemeColor(
                        lightTheme.textColorLayer1,
                        darkTheme.textColorLayer1
                      ),
                      minWidth: 0,
                      maxWidth: {
                        xs: "150px",
                        sm: "200px",
                        md: "400px",
                        lg: "400px",
                      },
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: "0 1 auto",
                    }}
                  >
                    {subtitleFile.displayName || subtitleFile.fileName}
                  </Typography>
                  <Button
                    variant="text"
                    onClick={() =>
                      handleViewDocument(subtitleFile.fileId, subtitleFile.id)
                    }
                    disabled={viewingFileId === subtitleFile.id}
                    sx={{
                      color: "#1976D2",
                      textTransform: "none",
                      fontSize: "14px",
                      fontWeight: 500,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5,
                      px: { xs: 1, sm: 1.5 },
                      flexShrink: 0,
                      ml: { xs: 0, sm: 1 },
                      "&:hover": {
                        backgroundColor: "transparent",
                      },
                      "&:disabled": {
                        color: "#9E9E9E",
                      },
                    }}
                    endIcon={<ArrowForwardIcon sx={{ fontSize: "16px" }} />}
                  >
                    {viewingFileId === subtitleFile.id
                      ? "Opening..."
                      : "View document"}
                  </Button>
                  <IconButton
                    onClick={() => handleFileDelete(subtitleFile.id)}
                    disabled={deletingFileId === subtitleFile.id}
                    sx={{
                      color: "error.main",
                      flexShrink: 0,
                      padding: { xs: "8px", sm: "12px" },
                      ml: { xs: 0.5, sm: 1 },
                      "&:disabled": {
                        color: "#9E9E9E",
                      },
                    }}
                  >
                    <DeleteOutlineOutlinedIcon
                      sx={{ fontSize: { xs: "16px", sm: "18px" } }}
                    />
                  </IconButton>
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default SubtitleManagement;
